import { FunctionComponent, useContext, useState } from "react";
import { CartItemType, UpdateCartItemCommandType } from "types/CartTypes";
import {
  OtherProductDetailsType,
  ProductVMType,
  UpdateOtherProductType,
} from "types/products/ProductCommandsType";
import FileResourceList from "components/FileResourceList";
import Modal from "components/Modals/Modal";
import { apiUpdateProduct } from "util/network/Products";
import { apiUpdateCartItem } from "util/network/Carts";
import AppStateContext from "contexts/AppStateContext";
import { FileResourceType } from "types/FileResourceTypes";
import {
  Badge,
  IconButton,
  SvgIcon,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Delete, FileUpload } from "@mui/icons-material";
import React from "react";
import { useToast } from "contexts/ToastContext";
import AmountInput from "./AmountInput";
import { formatter } from "util/TextUtil";
import Checkbox from "components/Checkbox/Checkbox";
import styles from "./Styles/cartTableRowStyle.module.css";
import EditableAdornment from "./EditableAdornment";

type PropsType = {
  cartId: number;
  item: CartItemType;
  onChange: (item: CartItemType) => void;
  deleteItem: (item: CartItemType) => Promise<boolean>;
  disabled?: boolean;
  isChecked: boolean;
  index: number;
  onCheckedChange: (index: number, isChecked: boolean) => void;
};

const OtherItemTableRow: FunctionComponent<PropsType> = React.memo(
  ({ cartId, item, onChange, deleteItem, disabled = false, isChecked, index, onCheckedChange }) => {
    const { token } = useContext(AppStateContext);
    const { product } = item;
    const details = product.details as OtherProductDetailsType;

    const [name, setName] = useState<string>(product.name);
    const [nameDraft, setNameDraft] = useState<string>(product.name);

    const [description, setDescription] = useState<string>(details.description ?? "");
    const [descriptionDraft, setDescriptionDraft] = useState<string>(details.description ?? "");

    const [priceStandard, setPriceStandard] = useState<number>(product.priceStandard);
    const [priceStandardText, setPriceStandardText] = useState<string>(String(product.priceStandard));
    const [weightSaved, setWeightSaved] = useState<number>(product.weight);
    const [weightText, setWeightText] = useState<string>(String(product.weight));
    const [quantity, setQuantity] = useState<number>(item.quantity);
    const [total, setTotal] = useState<number>(item.total);
    const [fileResources, setFileResources] = useState<FileResourceType[]>(details.fileResources);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [showFileModal, setShowFileModal] = useState<boolean>(false);

    const { addToast } = useToast();

    const updateProduct = (command: UpdateOtherProductType): Promise<ProductVMType>=> {
      setIsDisabled(true);
      return apiUpdateProduct(token, product.id, command)
        .then((res) => {
          const deets = res.details as OtherProductDetailsType;
          setName(res.name);
          setNameDraft(res.name);
          setDescription(deets.description ?? "");
          setDescriptionDraft(deets.description ?? "");
          setPriceStandard(res.priceStandard);
          setPriceStandardText(String(res.priceStandard));
          setWeightSaved(res.weight);
          setWeightText(String(res.weight));
          return res;
        })
        .catch((err) => {
          addToast({ type: "error", message: `Could not update product: ${err}`, keep: true });
          throw err;
        })
        .finally(() => setIsDisabled(false));
    };

    const updateItem = (input: UpdateCartItemCommandType) => {
      setIsDisabled(true);
      return apiUpdateCartItem(token, cartId, item.id, input)
        .then((res) => {
          setTotal(res.total);
          setQuantity(res.quantity);
          onChange(res);
          return res;
        })
        .catch((err) => {
          addToast({ type: "error", message: `Could not update cart item: ${err}`, keep: true });
        })
        .finally(() => setIsDisabled(false));
    };

    const saveName = () => { if (nameDraft !== name) updateProduct({ name: nameDraft }); };
    const discardName = () => setNameDraft(name);

    const saveDescription = () => { if (descriptionDraft !== description) updateProduct({ description: descriptionDraft }); };
    const discardDescription = () => setDescriptionDraft(description);

    const saveProductPrice = () => {
      const parsed = parseFloat(priceStandardText.trim().replace(",", "."));
      if (isNaN(parsed)) { setPriceStandardText(String(priceStandard)); return; }
      updateProduct({ price: parsed }).then((res) => {
        updateItem({ pricePerUnit: res.priceStandard });
      });
    };
    const discardProductPrice = () => setPriceStandardText(String(priceStandard));

    const saveWeight = () => {
      const parsed = parseFloat(weightText.trim().replace(",", "."));
      if (isNaN(parsed)) { setWeightText(String(weightSaved)); return; }
      updateProduct({ weight: parsed });
    };
    const discardWeight = () => setWeightText(String(weightSaved));

    const updateAmount = (amount: number) => updateItem({ quantity: amount });

    const handleFileResourceChange = (resources: FileResourceType[]) => {
      setFileResources(resources);
      return apiUpdateProduct(token, product.id, { fileResources: resources.map((x) => x.id) })
        .then(() => {
          onChange({ ...item, product: { ...product, details: { ...details, fileResources: resources } } });
          return true;
        })
        .catch((err) => {
          addToast({ type: "error", message: `Could not update files: ${err}`, keep: true });
          return false;
        });
    };

    return (
      <TableRow className={styles.cellsNoWrap} sx={{ backgroundColor: isChecked ? "#e7f3ff" : "" }}>

        <TableCell align="left" sx={{ padding: 0 }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <Checkbox
              checked={isChecked}
              onChange={(e) => onCheckedChange(index, e.target.checked)}
            />
            {`\u00A0`}
            <span>{product.id}</span>
          </div>
        </TableCell>

        <TableCell>
          <TextField
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onFocus={() => setNameDraft(name)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { saveName(); (e.target as HTMLInputElement).blur(); }
              if (e.key === "Escape") { discardName(); (e.target as HTMLInputElement).blur(); }
            }}
            disabled={isDisabled || disabled}
            InputProps={{
              endAdornment: (
                <EditableAdornment
                  isDirty={nameDraft !== name}
                  onSave={saveName}
                  onDiscard={discardName}
                />
              ),
            }}
          />
        </TableCell>

        <TableCell colSpan={2}>
          <TextField
          sx={{ width: "90%" }}
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value)}
            onFocus={() => setDescriptionDraft(description)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { saveDescription(); (e.target as HTMLTextAreaElement).blur(); }
              if (e.key === "Escape") { discardDescription(); (e.target as HTMLTextAreaElement).blur(); }
            }}
            multiline
            rows={2}
            disabled={isDisabled || disabled}
            InputProps={{
              endAdornment: (
                <EditableAdornment
                  isDirty={descriptionDraft !== description}
                  onSave={saveDescription}
                  onDiscard={discardDescription}
                />
              ),
            }}
          />
        </TableCell>

        <TableCell>
          <TextField
            value={weightText}
            onChange={(e) => setWeightText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { saveWeight(); (e.target as HTMLInputElement).blur(); }
              if (e.key === "Escape") { discardWeight(); (e.target as HTMLInputElement).blur(); }
            }}
            sx={{ width: weightText !== String(weightSaved) ? "20ch" : "12ch" }}
            InputProps={{
              endAdornment: (
                <EditableAdornment
                  isDirty={weightText !== String(weightSaved)}
                  onSave={saveWeight}
                  onDiscard={discardWeight}
                  defaultAdornment="g"
                />
              ),
            }}
            disabled={isDisabled || disabled}
          />
        </TableCell>

        <TableCell>
          <Tooltip title={<h3>UPLOAD FILES</h3>}>
            <IconButton onClick={() => setShowFileModal(true)}>
              <Badge badgeContent={fileResources.length} color="primary">
                <SvgIcon><FileUpload /></SvgIcon>
              </Badge>
            </IconButton>
          </Tooltip>
          <Modal isOpen={showFileModal} onClose={() => setShowFileModal(false)}>
            <div style={{ height: "100%", overflow: "auto" }}>
              <FileResourceList
                resources={fileResources}
                allowEdit={!disabled}
                onChange={handleFileResourceChange}
              />
            </div>
          </Modal>
        </TableCell>

        <TableCell align="center">
          <AmountInput
            initVal={quantity}
            onChange={updateAmount}
            direction="column-reverse"
            disabled={isDisabled || disabled}
          />
        </TableCell>

        <TableCell align="center">
          <TextField
            value={priceStandardText}
            onChange={(e) => setPriceStandardText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { saveProductPrice(); (e.target as HTMLInputElement).blur(); }
              if (e.key === "Escape") { discardProductPrice(); (e.target as HTMLInputElement).blur(); }
            }}
            sx={{ m: 1, width: "19ch" }}
            InputProps={{
              endAdornment: (
                <EditableAdornment
                  isDirty={priceStandardText !== String(priceStandard)}
                  onSave={saveProductPrice}
                  onDiscard={discardProductPrice}
                  defaultAdornment="kr"
                />
              ),
            }}
            disabled={isDisabled || disabled}
          />
        </TableCell>

        <TableCell>{formatter.format(total ?? 0)}</TableCell>

        <TableCell>
          <Tooltip title={<h5>SLET</h5>} placement="bottom">
            <IconButton
              aria-label="Delete"
              color="secondary"
              onClick={() => deleteItem(item)}
              disabled={isDisabled || disabled}
            >
              <Delete fontSize="large" />
            </IconButton>
          </Tooltip>
        </TableCell>

      </TableRow>
    );
  }
);

export default OtherItemTableRow;
